import { displayUnitOf, formatAxisDay } from "@heraion/shared";
import { useCallback, useState } from "react";
import { ScrollView } from "react-native";
import { useTranslation } from "react-i18next";
import type { BodyFigure, PlanStarter } from "@heraion/shared";
import {
  ProgressRecordsCard,
  ProgressView,
  RHYTHM_KIND_KEY,
  asyncStorageResourceCache,
  resolveTheme,
  useCatalogItems,
  useSessionsSummary,
  useTagVolumeRecords,
  type TagWeeks,
} from "@heraion/ui";
import { apiFetch } from "./api";
import { exerciseCatalog } from "./logger/exercisesCache";
import { notifyViaWindow } from "./confirm";
import { scheduleIdle } from "./progress/scheduleIdle";
import { shareOrDownloadPngs } from "./logger/shareCardCaptureWeb";
import { useExerciseTrackerProps, useProgressLayoutProps } from "./useHomeLayoutProps";
import { bodyweightOf, useShellProfile } from "./ThemeProfileBridge";
import { useSelfProgressDeps } from "./progress/progressDeps";
import { useSelfAthleteTargets } from "./progress/useSelfAthleteTargets";
import { useSelfCoverageDeps } from "./progress/useSelfCoverageDeps";
import { useThemePalette } from "./theme";
import { useWorkoutStarter } from "./useWorkoutStarter";
import { useAccountKey } from "./auth/useAccountKey";

// #397: the tag-volume weeks for offline, mobile's key and shape.
const tagWeeksCache = asyncStorageResourceCache<TagWeeks>("progress/tagWeeks");

// THE Progress surface on web-rnw — the SAME shared ProgressView mobile's
// Progress tab mounts (the coverage census + supporting panels + Tier-2 metrics +
// session stats + frequency chart). The panels self-fetch through apiFetch.
// The volume-by-tag records card is now the SAME shared card mobile mounts
// (lift/progress-tag-volume-web, which unblocked RW-8). painMap comes from
// useSelfProgressDeps (BodyScreen's PainMapPicker writes it, lifted
// 2026-08-16): the pain panel + alert escalation now see real data. See
// next/rnw-strangler.md.

const formatDate = (iso: string, locale: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return formatAxisDay(d, locale);
};

export function ProgressScreen({
  onStartWorkout,
}: {
  // AP-9b: the coverage card's «Build today's session» hands its starter to
  // AppShell, which opens the logger (the Plan surface's seam).
  onStartWorkout?: (starter?: PlanStarter) => void;
} = {}) {
  const { dark, family } = useThemePalette();
  const { t, i18n } = useTranslation();
  const theme = resolveTheme(dark, family);
  const { doc, savePatch } = useShellProfile();
  const weekStartDay = doc?.["weekStartDay"] as number | undefined;
  const { rhythm, weekStreak, error } = useSessionsSummary(
    apiFetch,
    weekStartDay,
  );
  // #321: the Rhythm's Strength / Cardio / All, per account.
  const rhythmKindKey = useAccountKey(RHYTHM_KIND_KEY);
  const layoutProps = useProgressLayoutProps(doc, savePatch);
  const trackerProps = useExerciseTrackerProps(doc, savePatch);
  // Catalog for the tag-volume card: the shared picker hook, straight to
  // network here (no cache passed) like the rest of web Progress.
  const { raw: catalog } = useCatalogItems({ catalog: exerciseCatalog });
  const figure = doc?.["preferredFigure"] as BodyFigure | undefined;

  // The shared self base (apiFetch, theme, units, resultsCache, isDark,
  // weekStartDay) + the PG-1 extras this surface computes on top.
  const base = useSelfProgressDeps();
  const bodyweightKg = bodyweightOf(doc);
  const toLogger = useCallback(
    (starter: PlanStarter) => onStartWorkout?.(starter),
    [onStartWorkout],
  );
  const { startBuiltSession } = useWorkoutStarter(null, bodyweightKg, toLogger);
  // AP-9b (Antenore, option a): the athlete's own targets, as mobile's
  // useProgressDeps passes them. Without them the coverage always ran
  // degraded on web, so the below-MEV sentence and its link never showed.
  const { value: athleteTargets } = useSelfAthleteTargets();
  const deps = useSelfCoverageDeps(base, {
    figure,
    athleteTargets,
    startBuiltSession: onStartWorkout ? startBuiltSession : undefined,
  });

  // Volume-by-tag records card — the SAME shared card + data hook mobile
  // Progress mounts (lift/progress-tag-volume-web). The fetch competes with
  // everything else on this mount, so it's deferred the browser way
  // (scheduleIdle) instead of mobile's InteractionManager. Its weeks are kept
  // on disk for offline, as on mobile (#397).
  // PG-39: only start the fetch once the card is actually shown, its
  // widget on and open (PG-57).
  const [recordsOpened, setRecordsOpened] = useState(false);
  const { chartError, tagWeeks } = useTagVolumeRecords({
    apiFetch,
    catalog,
    weekStartDay,
    enabled: recordsOpened,
    schedule: scheduleIdle,
    cache: tagWeeksCache,
  });
  const displayUnit = displayUnitOf(doc);

  // PG-64: the report's pages to the Web Share API, else a download each,
  // numbered in page order.
  const shareReportImage = useCallback(
    (uris: string[], title: string, fileStem: string) => {
      const stem = `${t("brand.product").toLowerCase()}-${fileStem}`;
      return shareOrDownloadPngs(
        uris,
        uris.map((_, i) => (uris.length > 1 ? `${stem}-${i + 1}.png` : `${stem}.png`)),
        title,
      );
    },
    [t],
  );
  const onShareReportError = useCallback(
    (msg: string) =>
      notifyViaWindow(
        t("mobile.postSession.shareErrorTitle", { defaultValue: "Share failed" }),
        msg,
      ),
    [t],
  );

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: theme.palette.bg }}
      contentContainerStyle={{ padding: 16, gap: 16 }}
    >
      <ProgressView
        deps={deps}
        rhythm={rhythm}
        rhythmKindKey={rhythmKindKey}
        weekStreak={weekStreak}
        summaryError={error}
        formatDate={formatDate}
        locale={i18n.language}
        {...layoutProps}
        {...trackerProps}
        onShareReportImage={shareReportImage}
        onShareReportError={onShareReportError}
        recordsSlot={
          <ProgressRecordsCard
            tagWeeks={tagWeeks}
            chartError={chartError}
            displayUnit={displayUnit}
            locale={i18n.language}
            theme={theme}
            onExpand={() => setRecordsOpened(true)}
          />
        }
      />
    </ScrollView>
  );
}
